// transactionsDataGrid.columns.tsx
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-pro'
import { useQuery } from '@tanstack/react-query'
import {
  base_colorAlert,
  text_colorTextSecondary,
  warning_text,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_family_weight_italic, font_sm } from 'ext-ui/style/typography'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { approvalActionsCellColDef } from 'src/components/DataGrid/cells/ApprovalActionsCell/ApprovalActionsCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { hoverActionsCellColDef } from 'src/components/DataGrid/cells/HoverActionsCell/HoverActionsCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import TextAndPillCell from 'src/components/DataGrid/cells/TextAndPillCell/TextAndPillCell'
import ManagerReviewIconCell from 'src/components/MangerReviewIconCell/MangerReviewIconCell'
import ReceiptPushStatusTooltip from 'src/components/ReceiptPushStatusTooltip/ReceiptPushStatusTooltip'
import ReceiptsTooltip from 'src/components/ReceiptsTooltip/ReceiptsTooltip'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SingleReceiptReminder from 'src/components/SingleReceiptReminder/SingleReceiptReminder'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import TransactionPushStatusTooltip from 'src/components/TransactionPushStatusTooltip/TransactionPushStatusTooltip'
import FinalApprovalDropdownCell from 'src/components/TransactionsDataGrid/cells/FinalApprovalDropdownCell'
import ReceiptAgentStatusCell from 'src/components/TransactionsDataGrid/cells/ReceiptAgentStatusCell'
import ReceiptVerificationCell from 'src/components/TransactionsDataGrid/cells/ReceiptVerificationCell'
import TransactionExpenseCategoryCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryCell'
import TransactionExpenseCategoryDropdownCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryDropdownCell'
import { getReceiptAgentTablePresentation } from 'src/components/TransactionsDataGrid/receiptAgentTablePresentation'
import { getConfiguredColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.config'
import {
  ExpenseCategoryFieldKey,
  TransactionsDataGridRowModel,
} from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import {
  AvatarSizes,
  OrganizationConnectedPlatform,
  OrganizationRoles,
} from 'src/enums'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import Crncy from 'src/localization/Crncy'
import { translateNode } from 'src/localization/translateNode'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import {
  rollupStatusFromAttachments,
  verificationFromMatchingStatuses,
  verificationSortKey,
} from 'src/models/receiptVerification.models'
import {
  aggregateVerificationReasons,
  mockTransactionVerificationStatus,
} from 'src/models/receiptVerificationMock'
import {
  TransactionDateType,
  TransactionListModel,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  type ExpenseCategoryResponse,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { useReceiptVerificationEnabled } from 'src/queries/organizations/receiptVerification.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import {
  PostSingleTransactionExpenseCategoryVariables,
  usePostSingleTransactionExpenseCategory,
} from 'src/queries/transactions.queries'
import { useMutationStateTracker } from 'src/queries/useMutationStateTracker'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeCurrencyFromLocale } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

const colorForTransaction = (
  tx: TransactionListModel,
): undefined | 'highlight' | 'severe' => {
  if (tx?.isDeclined) return 'severe'
  if (tx?.isCredit) return 'highlight'
  return undefined
}

const cellPropsForTransaction = (
  _fieldName: string,
  tx: TransactionListModel,
  bold?: boolean,
  alwaysDefaultColor?: boolean,
) => ({
  color: alwaysDefaultColor ? undefined : colorForTransaction(tx),
  disabled: tx.isPending,
  bold,
})

type UseTransactionsColumnsParams = {
  context?: TransactionReportType
  checkboxMinWidth?: number // e.g. props.checkboxProps ? 115 : 150
  filters: TransactionsFiltersBody
  rowApprovalActions?: (row: any) => React.ReactElement
  rowHoverActions?: (row: any) => React.ReactElement
}

type UseTransactionsColumnsResult = {
  columns: Array<GridColDef<TransactionsDataGridRowModel>>
  pinnedLeft: string[]
  pinnedRight: string[]
  ecLoaded: boolean
  orgExpCats: ExpenseCategoryResponse[] | undefined
}

export function useTransactionsColumns(
  params: UseTransactionsColumnsParams,
): UseTransactionsColumnsResult {
  const {
    context,
    checkboxMinWidth = 150,
    filters,
    rowApprovalActions,
    rowHoverActions,
  } = params

  const translate = useTranslate()
  const currency = useSelector(selectNullSafeCurrencyFromLocale)
  const featureFlags = useSelector(selectFeatureFlaggerFromState)
  const receiptVerificationUiEnabled = useReceiptVerificationEnabled()
  const receiptAgentTransactionListEnabled =
    featureFlags.enable_receipt_agent_transaction_list
  const transactionListReceiptThumbnailsEnabled =
    featureFlags.enable_transaction_list_receipt_thumbnails
  const receiptAgentMockDataEnabled =
    receiptAgentTransactionListEnabled &&
    featureFlags.enable_receipt_recovery_mock_data
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const users = useSelector(selectUserListModelsFromState)
  const isQuickbooks =
    (activeIntegration !== undefined &&
      [
        OrganizationConnectedPlatform.QUICKBOOKS_ONLINE,
        OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP,
        OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX,
      ].includes(activeIntegration.connectedPlatform)) ||
    false

  const isClearedAtSelected = filters?.dateType === TransactionDateType.Clear

  const { data: user } = useQueryOwnUser({})
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(user?.organizationId),
  )
  const { SEND_ORGANIZATION_RECEIPT_REMINDER: canSendReceiptReminder } =
    useQueryUserFeatures([UserFeature.SEND_ORGANIZATION_RECEIPT_REMINDER])
      .data || {}
  const { data: orgExpCats, isSuccess: isOrgExpCatsSuccess } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })
  const { data: departmentsList } = useQueryDepartments({
    // NOT `|| ''`. The factory guards with `enabled: organizationId !== undefined`
    // and its queryFn throws on a falsy id, so an empty string passes the guard
    // and then throws — three retries and a logged failure on every mount for a
    // user with no organization (an RCN or budget owner). `undefined` is what
    // the option is typed for and what actually disables the query.
    organizationId: user?.organizationId,
    includeArchived: true,
  })

  // Build helpers that depend on store data
  const activeTransactionConnection = useCallback(
    (tx: TransactionListModel) =>
      tx?.connectedPlatforms?.find(
        (c) => c.platform === activeIntegration?.connectedPlatform,
      ),
    [activeIntegration?.connectedPlatform],
  )

  const { mutateAsync: postCategoryLabel } =
    usePostSingleTransactionExpenseCategory()

  const { getMutationsFor } =
    useMutationStateTracker<PostSingleTransactionExpenseCategoryVariables>({
      mutationKey: transactionsQueryKeys.postSingleExpenseCategory(),
      status: 'pending',
    })

  /** Build static columns */
  /* Note: any column added here should also be added to
   * the columnVisibilityModel in TransactionsDataGrid.tsx
   * to ensure that column visibility is persisted correctly
   */
  const staticCols = useMemo<Array<GridColDef<TransactionsDataGridRowModel>>>(
    () => [
      {
        field: 'date',
        headerName:
          context === TransactionReportType.AccountingTransactions ||
          context === TransactionReportType.ExpenseReport
            ? translate('Clear Date')
            : translate('Date'),
        sortable: false,
        groupable: false,
        minWidth: checkboxMinWidth,
        renderCell: (params) => {
          const tx = params.row.transaction
          const dateText =
            tx?.isPending && isClearedAtSelected
              ? translateNode('Pending')
              : tx?.isReversal && isClearedAtSelected
                ? translateNode('Reversal')
                : isClearedAtSelected
                  ? tx?.dateClearedString
                  : tx?.dateString
          return (
            <DataGridCell
              text={dateText}
              params={params}
              {...cellPropsForTransaction('date', tx, true, true)}
            />
          )
        },
      },
      {
        field: 'merchant',
        headerName: translate('Merchant'),
        flex: 1,
        sortable: false,
        minWidth: 190,
        renderCell: (params) => {
          const tx = params.row.transaction
          const merchantImageSrc = tx.merchantImageSrc('small')
          return (
            <DataGridCell
              text={tx.normalizedMerchantNameOrUnknown}
              subtext={tx.mccGroupIfKnownMerchant}
              icon={
                <MerchantIcon
                  size={32}
                  merchantImgSrc={merchantImageSrc}
                  mccGroupKey={tx.mccGroupKey ?? 'OTHER'}
                />
              }
              params={params}
              {...cellPropsForTransaction('merchant', tx, true, true)}
            />
          )
        },
      },
      {
        field: 'originalMerchant',
        headerName: translate('Original Merchant'),
        sortable: false,
        minWidth: 150,
        renderCell: (params): React.ReactElement => {
          const transaction = params.row.transaction
          return (
            <DataGridCell
              text={transaction.merchantNameOrUnknown}
              subtext={transaction.mccGroupIfKnownMerchant}
              params={params}
              {...cellPropsForTransaction('merchant', transaction, true, true)}
            />
          )
        },
      },
      {
        field: 'card',
        headerName: translate('Card'),
        sortable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <TextAndPillCell
              text={tx.vcnDisplayName}
              pillText={tx.vcnLast4}
              secondPillText={
                !tx.isVirtual ? translate('Physical Card') : undefined
              }
              params={params}
              {...cellPropsForTransaction('card', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'cardUser',
        headerName: translate('Card User'),
        sortable: false,
        flex: 1,
        minWidth: 161,
        renderCell: (params) => {
          const tx = params.row.transaction
          const cardUserName =
            tx.nameOnCard ??
            tx.recipientName ??
            tx.cardholderName ??
            tx.recipientEmail
          const cardUser = users[tx.recipientId] ?? {
            id: tx.recipientId,
            fullNameOrEmail: cardUserName,
            initials: cardUserName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toUpperCase(),
          }
          return (
            <DataGridCell
              text={cardUserName}
              icon={
                <UserAvatar
                  decorative
                  size={AvatarSizes.Medium}
                  user={cardUser}
                />
              }
              params={params}
              {...cellPropsForTransaction('cardUser', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'team',
        headerName: translate('Team'),
        sortable: false,
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          const name = departmentsList?.find((d) => d.id === tx.teamId)?.name
          return (
            <DataGridCell
              text={name}
              params={params}
              {...cellPropsForTransaction('team', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'cardholderEmail',
        headerName: translate('Card User Email'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx?.cardholderEmail}
              params={params}
              {...cellPropsForTransaction('cardholderEmail', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'managerReviewStatus',
        headerName: translate('Manager'),
        sortable: false,
        minWidth: 100,
        renderCell: (params) => {
          const tx = params.row.transaction

          // Display "--" if there is no managerId or managerReviewId, meaning no manager assigned
          if (
            tx?.managerId === undefined &&
            tx?.managerReviewId === undefined
          ) {
            return (
              <DataGridCell
                params={params}
                text={
                  <div
                    css={css`
                      min-width: 24px;
                      text-align: center;
                      width: 100%;
                    `}
                  >
                    {'--'}
                  </div>
                }
                disabled
              />
            )
          }
          return (
            <DataGridCellWrapper params={params}>
              <ManagerReviewIconCell
                status={tx?.managerReviewStatus}
                rejectionReason={tx?.managerRejectionReason}
              />
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'finalReview',
        headerName: translate('Final Approval'),
        sortable: false,
        minWidth: 160,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <FinalApprovalDropdownCell
              filters={filters}
              params={params}
              status={tx.reviewStatus}
            />
          )
        },
      },
      {
        field: 'amount',
        align: 'left',
        headerName: translate('Amount'),
        sortable: false,
        minWidth: 100,
        renderCell: (params) => {
          const tx = params.row.transaction
          const isRejected =
            tx?.managerReviewStatus === TransactionManagerReviewStatus.Rejected
          return (
            <DataGridCell
              text={
                <div
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                  `}
                >
                  {isRejected && (
                    <SvgFlagNew
                      fill={base_colorAlert}
                      width={16}
                      height={16}
                      aria-hidden="true"
                      css={css`
                        display: block;
                        flex: 0 0 16px;
                      `}
                    />
                  )}
                  {tx.clearingOrAuthLabel(true)}
                </div>
              }
              subtext={!tx?.isNonCreditCleared && tx.pseudoStatusLabelRowView}
              params={params}
              {...cellPropsForTransaction('amount', tx, false, false)}
            />
          )
        },
      },
      {
        field: 'currency',
        headerName: translate('Currency'),
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        hideable: false,
        headerClassName: 'hideRightSeparator',
        width: 20,
        renderHeader: () => <></>,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authBillingCurrency !== currency && tx.authBillingCurrency
              }
              params={params}
              {...cellPropsForTransaction('currency', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'authBillingAmountCents',
        align: 'right',
        headerName: translate('Billing Amount'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authBillingAmountCents && (
                  <Crncy
                    currency={currency}
                    amount={toDollars(tx.authBillingAmountCents)}
                  />
                )
              }
              params={params}
              {...cellPropsForTransaction(
                'authBillingAmountCents',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authBillingCurrency',
        headerName: translate('Billing Currency'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx?.authBillingCurrency}
              params={params}
              {...cellPropsForTransaction(
                'authBillingCurrency',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authMerchantAmountCents',
        align: 'right',
        headerName: translate('Merchant Amount'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authMerchantAmountCents && (
                  <Crncy
                    currency={currency}
                    amount={toDollars(tx.authMerchantAmountCents)}
                  />
                )
              }
              params={params}
              {...cellPropsForTransaction(
                'authMerchantAmountCents',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authMerchantCurrency',
        headerName: translate('Merchant Currency'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.authMerchantCurrency}
              params={params}
              {...cellPropsForTransaction(
                'authMerchantCurrency',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authExchangeRate',
        headerName: translate('Exchange Rate'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.authExchangeRate}
              params={params}
              {...cellPropsForTransaction('authExchangeRate', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'attachmentsCount',
        headerName: translate('Number of Attachments'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.attachmentsCount}
              params={params}
              {...cellPropsForTransaction('attachmentsCount', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'creditCardDisplayName',
        headerName: translate('Funding Source'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.creditCardDisplayName}
              params={params}
              {...cellPropsForTransaction(
                'creditCardDisplayName',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'creditCardType',
        headerName: translate('Type of Account'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          const label =
            tx?.creditCardType === CreditCardType.Delegate
              ? 'Budget'
              : tx?.creditCardType !== CreditCardType.Source
                ? 'Physical Card'
                : ''
          return (
            <DataGridCell
              text={label}
              params={params}
              {...cellPropsForTransaction('creditCardType', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'receipts',
        headerName: translate('Receipts'),
        resizable: false,
        sortable: receiptVerificationUiEnabled,
        minWidth: 100,
        valueGetter: (_v, row) => {
          if (!receiptVerificationUiEnabled) return '0'
          const tx = row.transaction
          const receiptAgentPresentation = getReceiptAgentTablePresentation(
            tx,
            receiptAgentMockDataEnabled,
          )
          const thumbnailUrl = transactionListReceiptThumbnailsEnabled
            ? receiptAgentPresentation.thumbnailUrl
            : undefined
          const hasAny =
            (tx?.attachmentsCount ?? 0) > 0 || thumbnailUrl !== undefined
          if (!hasAny) return String(verificationSortKey(undefined))
          const matchingVerification = verificationFromMatchingStatuses(
            tx?.receiptMatchingStatuses,
          )
          const status =
            receiptAgentPresentation.receiptVerificationStatus ??
            rollupStatusFromAttachments(tx?.attachments) ??
            matchingVerification?.status ??
            mockTransactionVerificationStatus(tx?.id, tx?.attachmentsCount)
          const reasons = matchingVerification?.reasons?.join(',') ?? ''
          return `${verificationSortKey(status)}:${status ?? ''}:${reasons}`
        },
        renderCell: (params) => {
          const tx = params.row.transaction
          const receiptAgentPresentation = getReceiptAgentTablePresentation(
            tx,
            receiptAgentMockDataEnabled,
          )
          const thumbnailUrl = transactionListReceiptThumbnailsEnabled
            ? receiptAgentPresentation.thumbnailUrl
            : undefined
          const hasReceiptThumbnail = thumbnailUrl !== undefined
          const hasAny = (tx?.attachmentsCount ?? 0) > 0 || hasReceiptThumbnail
          const needs =
            receiptManagementEnabled &&
            tx?.attachmentsCount === 0 &&
            tx?.receiptRequired
          const matchingVerification = verificationFromMatchingStatuses(
            tx?.receiptMatchingStatuses,
          )
          const rollupStatus = rollupStatusFromAttachments(tx?.attachments)
          const verificationStatus =
            receiptAgentPresentation.receiptVerificationStatus ??
            rollupStatus ??
            matchingVerification?.status ??
            mockTransactionVerificationStatus(tx?.id, tx?.attachmentsCount)
          const verificationReasons = receiptVerificationUiEnabled
            ? (matchingVerification?.reasons ??
              aggregateVerificationReasons(
                tx?.id,
                tx?.attachmentsCount,
                tx?.attachments,
                verificationStatus,
              ))
            : undefined
          return hasAny || needs ? (
            <DataGridCellWrapper params={params}>
              {hasReceiptThumbnail && (
                <ReceiptVerificationCell
                  status={verificationStatus}
                  thumbnailUrl={thumbnailUrl}
                />
              )}
              {hasAny &&
                !hasReceiptThumbnail &&
                receiptVerificationUiEnabled &&
                verificationStatus && (
                  <ReceiptVerificationCell
                    status={verificationStatus}
                    reasons={verificationReasons}
                    transactionId={tx?.id}
                  />
                )}
              {hasAny &&
                !hasReceiptThumbnail &&
                (!receiptVerificationUiEnabled || !verificationStatus) && (
                  <ReceiptsTooltip
                    count={tx.attachmentsCount}
                    placement={'top'}
                    transactionId={tx?.id}
                    verificationStatus={verificationStatus}
                    verificationReasons={verificationReasons}
                  />
                )}
              {needs && !canSendReceiptReminder && (
                <ReceiptsTooltip placement={'top'} isMissing />
              )}
              {needs && canSendReceiptReminder && (
                <SingleReceiptReminder
                  transactionId={tx?.id}
                  name={tx?.nameOnCard}
                  wrapText
                  maxWidth={150}
                />
              )}
            </DataGridCellWrapper>
          ) : (
            <></>
          )
        },
      },
      ...(receiptAgentTransactionListEnabled
        ? [
            {
              field: 'receiptAgentStatus',
              headerName: translate('Receipt Agent'),
              sortable: false,
              minWidth: 156,
              renderCell: (
                params: GridCellParams<TransactionsDataGridRowModel>,
              ) => {
                const tx = params.row.transaction
                const receiptAgentPresentation =
                  getReceiptAgentTablePresentation(
                    tx,
                    receiptAgentMockDataEnabled,
                  )
                return (
                  <DataGridCellWrapper params={params}>
                    <ReceiptAgentStatusCell
                      status={receiptAgentPresentation.status}
                    />
                  </DataGridCellWrapper>
                )
              },
            },
          ]
        : []),
      {
        field: 'txnSynced',
        headerName: translate('Transactions Synced'),
        sortable: false,
        minWidth: 125,
        renderCell: (params) => {
          const tx = params.row.transaction
          const conn = activeTransactionConnection(tx)
          return (
            <DataGridCellWrapper params={params}>
              {user?.organizationRole !== OrganizationRoles.member && conn && (
                <TransactionPushStatusTooltip
                  placement={'left'}
                  status={conn?.syncStatus}
                  platform={conn?.platform}
                  connectedPlatformName={activeIntegration?.platformName ?? ''}
                />
              )}
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'receiptsSynced',
        headerName: translate('Receipts Synced'),
        sortable: false,
        minWidth: 125,
        renderCell: (params: any) => {
          const tx = params.row.transaction
          const conn = activeTransactionConnection(tx)
          return (
            <DataGridCellWrapper params={params}>
              {user?.organizationRole !== OrganizationRoles.member &&
                conn &&
                tx.receiptAttachmentSyncStatus && (
                  <ReceiptPushStatusTooltip
                    openLeft
                    status={tx.receiptAttachmentSyncStatus}
                    platform={conn?.platform}
                  />
                )}
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'status',
        headerName: translate('Review Status'),
        sortable: false,
        minWidth: 150,
        renderCell: (params) => (
          <PillCell color="pending" params={params}>
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <SvgClock stroke={warning_text} height={14} width={14} />
              {translate('Pending Review')}
            </span>
          </PillCell>
        ),
      },
    ],
    [
      translate,
      checkboxMinWidth,
      filters,
      isClearedAtSelected,
      departmentsList,
      users,
      currency,
      receiptManagementEnabled,
      receiptVerificationUiEnabled,
      receiptAgentMockDataEnabled,
      receiptAgentTransactionListEnabled,
      transactionListReceiptThumbnailsEnabled,
      canSendReceiptReminder,
      user?.organizationRole,
      activeIntegration?.platformName,
      activeTransactionConnection,
    ],
  )

  /** Dynamic Expense Category columns */
  const ecCols = useMemo<
    Array<GridColDef<TransactionsDataGridRowModel>>
  >(() => {
    if (!orgExpCats) return []
    const editable = context !== TransactionReportType.ExpenseReport
    return orgExpCats.map((cat) => ({
      field: `ec:${cat.id}` as ExpenseCategoryFieldKey,
      headerName: cat.name,
      sortable: false,
      minWidth: 200,
      editable,
      valueGetter: (_v, row) =>
        row.expenseCategoryCellData[`ec:${cat.id}`]?.labelIdOrFreeText,
      valueFormatter: (_v, row) =>
        row.expenseCategoryCellData[`ec:${cat.id}`]?.valueText,
      renderCell: (params: GridCellParams<TransactionsDataGridRowModel>) =>
        params.row.transaction.isSplit &&
        cat.type !== ExpenseCategoryType.Vendor &&
        !(isQuickbooks && cat.code == 'tc_DEPARTMENTS') ? (
          <span
            css={css`
              color: ${text_colorTextSecondary};
              ${font_sm}
              font-family: ${font_family_weight_italic};
              padding-left: ${spacing(6)};
            `}
          >
            {translate('Split')}
          </span>
        ) : (
          <TransactionExpenseCategoryCell
            params={params}
            onClear={() =>
              postCategoryLabel({
                transactionId: params.row.transaction.id,
                categoryId: cat.id,
                body: { labelId: undefined },
              }).catch(() => {
                /* swallow or surface via toast here if desired */
              })
            }
            loading={
              getMutationsFor({
                categoryId: cat.id,
                transactionId: params.row.transaction.id,
              }).length > 0
            }
            editable={editable && !params.row.transaction.isSplit}
          />
        ),
      renderEditCell: (params) => (
        <TransactionExpenseCategoryDropdownCell
          params={params}
          editMode="cell"
          category={cat}
          loading={
            getMutationsFor({
              categoryId: cat.id,
              transactionId: params.row.transaction.id,
            }).length > 0
          }
        />
      ),
    }))
  }, [orgExpCats, context, getMutationsFor, postCategoryLabel])

  /** Optional tail cells */
  const tailCols = useMemo<
    Array<GridColDef<TransactionsDataGridRowModel>>
  >(() => {
    const tail: GridColDef<TransactionsDataGridRowModel>[] = []
    if (rowApprovalActions)
      tail.push(approvalActionsCellColDef(translate, rowApprovalActions))
    if (rowHoverActions) tail.push(hoverActionsCellColDef(rowHoverActions))
    return tail
  }, [rowApprovalActions, rowHoverActions])

  /** Final columns list */
  const columns = React.useMemo(
    () => [...staticCols, ...ecCols, ...tailCols],
    [staticCols, ecCols, tailCols],
  )

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const configured = useMemo(
    () => getConfiguredColumns(context, columns, managerReviewRulesEnabled),
    [context, columns, managerReviewRulesEnabled],
  )
  const orderedColumns = configured.columns
  const pinnedLeft = configured.pinnedLeft
  const pinnedRight = configured.pinnedRight

  return {
    columns: orderedColumns,
    pinnedLeft,
    pinnedRight,
    ecLoaded: isOrgExpCatsSuccess,
    orgExpCats,
  }
}
